import { API_BASE_URL } from './api';

// Types for the clinics API
export interface ClinicInfo {
  agency_id: string;
  clinic_id: string;
  clinic_name: string;
  auto_mrn: boolean;
  physicians: Array<{
    name: string;
    id: string;
  }>;
  clinicians: Array<{
    name: string;
    id: string;
  }>;
  clinical_care_managers: Array<{
    name: string;
    id: string;
  }>;
  case_managers: Array<{
    name: string;
    id: string;
  }>;
  oasis_approvers: Array<{
    name: string;
    id: string;
  }>;
  pt_approvers: Array<{
    name: string;
    id: string;
  }>;
  ot_approvers: Array<{
    name: string;
    id: string;
  }>;
  st_approvers: Array<{
    name: string;
    id: string;
  }>;
  primary_schedulers: Array<{
    name: string;
    id: string;
  }>;
  secondary_schedulers: Array<{
    name: string;
    id: string;
  }>;
  insurance_providers?: Array<{
    name: string;
    id: string;
    type: number;
    payer_id: string;
    clearinghouse: string;
  }>;
  branches: Array<{
    name: string;
    id: string;
  }>;
}

export type ClinicInfoResponse = ClinicInfo[];

export interface AgencyInsuranceResponse {
  id: string;
  name: string;
  agency_id: string;
  payor_id?: string;
  agency_clinic_id?: string | null;
  edi_payer_id: string;
  edi_clearinghouse: string;
  clinic_key: string;
  clinic_name: string;
  insurance_key: string;
  visit_authorization_required?: boolean;
  noa_required?: boolean;
  display_in_auth_manager?: boolean;
  stedi_eligibility_enabled?: boolean | null;
  created_at: string;
  updated_at: string;
}

// Re-export from generated types — avoid manual interface duplication
import type {
  AgencyClinicResponse as _AgencyClinicResponse,
  AgencyClinicCreate as _AgencyClinicCreate,
  AgencyClinicUpdate as _AgencyClinicUpdate,
} from './api/types.gen';

export type AgencyClinicResponse = _AgencyClinicResponse;
export type AgencyClinicCreate = _AgencyClinicCreate;
export type AgencyClinicUpdate = _AgencyClinicUpdate;

/**
 * Get clinic information from RogerDoc API for a specific agency
 */
export const getClinicInfo = async (
  agencyId: string
): Promise<ClinicInfoResponse> => {
  // Build URL path with agency_id and optional clinic_id
  const url = `${API_BASE_URL}/rogerdoc/api/clinics/info/${encodeURIComponent(agencyId)}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch clinic info: ${response.statusText}`);
  }

  return response.json();
};
/**
 * Get agency insurance records
 */
export const getAgencyInsurances = async (
  agencyId?: string,
  agencyClinicId?: string,
  skip?: number,
  limit?: number
): Promise<AgencyInsuranceResponse[]> => {
  const params = new URLSearchParams();
  if (agencyId) {
    params.append('agency_id', agencyId);
  }
  if (agencyClinicId) {
    params.append('agency_clinic_id', agencyClinicId);
  }
  if (skip !== undefined) {
    params.append('skip', skip.toString());
  }
  if (limit !== undefined) {
    params.append('limit', limit.toString());
  }

  const response = await fetch(
    `${API_BASE_URL}/clinics/insurance?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch agency insurances: ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Update agency insurance records from clinic information
 */
export const updateInsurancesFromClinicInfo = async (
  clinicInfo: ClinicInfoResponse
): Promise<{
  deleted_count: number;
  created_count: number;
  message: string;
}> => {
  const response = await fetch(
    `${API_BASE_URL}/clinics/insurance/update-from-clinic-info`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(clinicInfo),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update insurances: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get agency clinics
 */
export const getAgencyClinics = async (
  agencyId?: string,
  skip: number = 0,
  limit: number = 100
): Promise<AgencyClinicResponse[]> => {
  const params = new URLSearchParams();
  if (agencyId) {
    params.append('agency_id', agencyId);
  }
  params.append('skip', skip.toString());
  params.append('limit', limit.toString());

  const response = await fetch(
    `${API_BASE_URL}/agency-clinics/?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch agency clinics: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get a specific agency clinic by ID
 */
export const getAgencyClinic = async (
  clinicId: string
): Promise<AgencyClinicResponse> => {
  const response = await fetch(`${API_BASE_URL}/agency-clinics/${clinicId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch agency clinic: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Create a new agency clinic
 */
export const createAgencyClinic = async (
  clinicData: AgencyClinicCreate
): Promise<AgencyClinicResponse> => {
  const response = await fetch(`${API_BASE_URL}/agency-clinics/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(clinicData),
  });

  if (!response.ok) {
    throw new Error(`Failed to create agency clinic: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Update an agency clinic
 */
export const updateAgencyClinic = async (
  clinicId: string,
  clinicData: AgencyClinicUpdate
): Promise<AgencyClinicResponse> => {
  const response = await fetch(`${API_BASE_URL}/agency-clinics/${clinicId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(clinicData),
  });

  if (!response.ok) {
    throw new Error(`Failed to update agency clinic: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Delete an agency clinic
 */
export const deleteAgencyClinic = async (clinicId: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/agency-clinics/${clinicId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete agency clinic: ${response.statusText}`);
  }
};

export interface AgencyInsuranceCreateRequest {
  agency_clinic_id: string;
  name: string;
  edi_payer_id?: string;
  edi_clearinghouse?: string;
  visit_authorization_required?: boolean;
  noa_required?: boolean;
}

export interface AgencyInsuranceUpdateRequest {
  name: string;
  edi_payer_id?: string | null;
  edi_clearinghouse?: string | null;
  visit_authorization_required?: boolean;
  noa_required?: boolean;
}

export interface PayorSearchResult {
  id: string;
  payor_name: string;
  payor_type: string;
  electronic_payor_id?: string | null;
  clearinghouse_payor_id?: string | null;
  stedi_display_name?: string | null;
}

/**
 * Create a new agency insurance record (Roger EMR only)
 */
export const createAgencyInsurance = async (
  data: AgencyInsuranceCreateRequest
): Promise<AgencyInsuranceResponse> => {
  const response = await fetch(`${API_BASE_URL}/clinics/insurance`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(
      err?.detail || `Failed to create insurance: ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Update an existing agency insurance record (Roger EMR only)
 */
export const updateAgencyInsurance = async (
  insuranceId: string,
  data: AgencyInsuranceUpdateRequest
): Promise<AgencyInsuranceResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/clinics/insurance/${insuranceId}`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(
      err?.detail || `Failed to update insurance: ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Delete an agency insurance record (Roger EMR only)
 */
export const deleteAgencyInsurance = async (
  insuranceId: string
): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/clinics/insurance/${insuranceId}`,
    {
      method: 'DELETE',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(
      err?.detail || `Failed to delete insurance: ${response.statusText}`
    );
  }
};

/**
 * Search payors from the Stedi payor database
 */
export const searchPayors = async (
  query: string,
  limit: number = 20
): Promise<PayorSearchResult[]> => {
  const params = new URLSearchParams({
    search_term: query,
    limit: limit.toString(),
  });
  const response = await fetch(
    `${API_BASE_URL}/payors/search?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to search payors: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Sync clinics and insurances from clinic information by fetching from RogerDoc API
 */
export const syncClinicsFromClinicInfo = async (): Promise<{
  clinics: {
    created_count: number;
    updated_count: number;
    skipped_count: number;
    total_count: number;
  };
  insurances: {
    created_count: number;
    updated_count: number;
    skipped_count: number;
    total_count: number;
  };
  message: string;
}> => {
  const response = await fetch(
    `${API_BASE_URL}/agency-clinics/sync-from-clinic-info`,
    {
      method: 'POST',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to sync clinics: ${response.statusText}`);
  }

  return response.json();
};
